import React, {FunctionComponent, useState} from 'react';
import gql from 'graphql-tag';
import {useLocation} from 'react-router';
import {DateTime, DateTimeFormatOptions} from 'luxon';
import {useQuery} from '@apollo/client';
import AttendeeRecord from './AttendeeRecord';
import CardPaginator from './card/CardPaginator';
import TitleNumber from './card/TitleNumber';
import TitleCaption from './card/TitleCaption';
import CardTitle from './card/CardTitle';
import Card from './card/Card';
import CardContent from './card/CardContent';
import PriceFormatter from '../../models/formatter/PriceFormatter';
import {getNumberParam} from '../../utils/Parameters';

const LatestRegistrations: FunctionComponent = () => {
  const QUERY_PARAM = 'regs';
  const location = useLocation();
  const activePage = getNumberParam(location.search, QUERY_PARAM, 1);

  const count = 10;
  const [skip, setSkip] = useState((activePage - 1) * count);
  const [total, setTotal] = useState(0);

  const handlePageSwitch = (page: number) => {
    const newSkip = (page - 1) * count;
    setSkip(newSkip);
  };

  const calculatePage = () => Math.floor(skip / count) + 1;

  const initialLoading = () => loading && total === 0;

  const {data, loading} = useQuery<AttendeeFeedData, AttendeeFeedVars>(query, {
    variables: {
      skip, count
    },
    onCompleted: (feedData) => setTotal(feedData.attendees.total)
  });
  const dateFormat: DateTimeFormatOptions = {
    month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric'
  };

  return (
    <Card loading={initialLoading()}>
      <CardTitle wide={true}>
        <TitleCaption>Latest <br/>registrations</TitleCaption>
        <TitleNumber value={total}/>
      </CardTitle>
      <CardContent noBorders={false} loading={loading}>
        {data && total !== 0 && data.attendees.attendees.map((attendee) => <AttendeeRecord key={attendee.id}
                                                                                           id={attendee.id}
                                                                                           name={attendee.name}
                                                                                           eventTitle={attendee.event.title}
                                                                                           date={DateTime.fromISO(attendee.created).toLocaleString(dateFormat)}
                                                                                           paid={attendee.isPaid}
                                                                                           invoice={attendee.paymentMethod === 'Invoice'}
                                                                                           amount={attendee.paidAmount ? PriceFormatter.format({
                                                                                             currency: attendee.paidCurrency,
                                                                                             amount: attendee.paidAmount
                                                                                           }) : undefined}
                                                                                           card={attendee.bankCard}/>)}
        {total === 0 &&
          <div className="text-gray-dark">You had no registrations in the last 7 days</div>
        }
      </CardContent>
      {data && <CardPaginator total={total} page={calculatePage()} perPage={data.attendees.count}
                              className="px-5"
                              queryParamName={QUERY_PARAM}
                              onNextClick={handlePageSwitch} onPrevClick={handlePageSwitch}/>}

    </Card>
  );
};

export default LatestRegistrations;

interface BankCard {
  brand: string
  last4: string
}

interface Attendee {
  id: number
  name: string
  paidAmount: number
  paidCurrency: string
  paymentMethod: string
  bankCard?: BankCard
  event: {
    id: number
    title: string
  }
  isPaid: boolean
  created: string
}

interface AttendeesFeed {
  total: number
  count: number
  skip: number
  attendees: Attendee[]
}

interface AttendeeFeedData {
  attendees: AttendeesFeed
}

interface AttendeeFeedVars {
  skip: number
  count: number
}

const query = gql`
  query LatestAttendees($skip: Int!, $count: Int!) {
    attendees(createdLessThan: "2 weeks", orderBy: created_desc, skip: $skip, count: $count) {
      total
      count
      skip

      attendees {
        id
        name
        paidAmount
        paidCurrency
        paymentMethod
        bankCard {
          brand
          last4
        }
        isPaid
        event {
          id
          title
        }
        created
      }
    }
  }
`;
